import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

// Exceptions to look at after sales invoices are uploaded (or at any time), as three grids:
//   1. customers whose outstanding bills add up to more than their credit limit
//   2. customers with more outstanding bills than the distributor allows
//   3. products the open bills leave short, or below the distributor's low-stock threshold
// The bills in grids 1 and 2 can be cancelled one by one or together.

const REVIEW = "/api/v1/sales-invoices/review";

const money = (value) =>
  value === null || value === undefined || value === "" ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const right = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

const TABS = [
  { key: "credit", label: "Over Credit Limit" },
  { key: "bills", label: "Too Many Outstanding Bills" },
  { key: "stock", label: "Stock Short / Low" },
];

/** One of the two customer grids: bills of customers over a limit, with select + cancel. */
const BillGrid = ({ kind, rows, selected, onToggle, onToggleAll, onCancelOne, cancelling }) => {
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.invoiceId));
  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th style={{ width: "36px" }}>
              <input type="checkbox" checked={allSelected} onChange={() => onToggleAll(rows, allSelected)} aria-label="Select all bills shown" />
            </th>
            <th style={{ textAlign: "left" }}>Customer</th>
            {kind === "credit" ? (
              <>
                <th style={right}>Credit Limit</th>
                <th style={right}>Total Outstanding</th>
                <th style={right}>Over Limit By</th>
              </>
            ) : (
              <>
                <th style={right}>Outstanding Bills</th>
                <th style={right}>Bills Over The Limit</th>
                <th style={right}>Total Outstanding</th>
              </>
            )}
            <th>Invoice #</th>
            <th>Invoice Date</th>
            <th style={right}>Invoice Amount</th>
            <th style={right}>Balance</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={11}>
                <div className="empty-state">
                  <span className="empty-state-title">
                    {kind === "credit" ? "No customer is over their credit limit" : "No customer has more outstanding bills than allowed"}
                  </span>
                </div>
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              // customer figures are shown once per customer, on their first bill
              const firstOfCustomer = index === 0 || rows[index - 1].customerId !== row.customerId;
              return (
                <tr key={row.invoiceId} style={row.overLimit ? { background: "var(--color-danger-soft, #fef3f2)" } : undefined}>
                  <td>
                    <input type="checkbox" checked={selected.has(row.invoiceId)} onChange={() => onToggle(row.invoiceId)} aria-label={`Select ${row.invoiceNumber}`} />
                  </td>
                  <td style={{ textAlign: "left", fontWeight: firstOfCustomer ? 600 : 400 }}>
                    {firstOfCustomer ? row.customerName : ""}
                    {firstOfCustomer && row.retailerCode ? <div className="page-subtitle">{row.retailerCode}</div> : null}
                  </td>
                  {kind === "credit" ? (
                    <>
                      <td style={right}>{firstOfCustomer ? money(row.creditLimit) : ""}</td>
                      <td style={right}>{firstOfCustomer ? money(row.totalOutstanding) : ""}</td>
                      <td style={{ ...right, color: "var(--color-danger)", fontWeight: 600 }}>{firstOfCustomer ? money(row.excessAmount) : ""}</td>
                    </>
                  ) : (
                    <>
                      <td style={right}>{firstOfCustomer ? row.outstandingBills : ""}</td>
                      <td style={{ ...right, color: "var(--color-danger)", fontWeight: 600 }}>{firstOfCustomer ? row.excessBills : ""}</td>
                      <td style={right}>{firstOfCustomer ? money(row.totalOutstanding) : ""}</td>
                    </>
                  )}
                  <td>
                    {row.invoiceNumber}
                    {row.recent ? (
                      <span className="badge badge-blue" style={{ marginLeft: "6px" }}>
                        This upload
                      </span>
                    ) : null}
                  </td>
                  <td>{row.invoiceDate}</td>
                  <td style={right}>{money(row.invoiceAmount)}</td>
                  <td style={right}>{money(row.balance)}</td>
                  <td>
                    <span className={`badge ${row.overLimit ? "badge-red" : "badge-gray"}`}>{row.overLimit ? "Over limit" : "Within limit"}</span>
                  </td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => onCancelOne(row)} disabled={cancelling}>
                      Cancel invoice
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

/**
 * `importRunId` — when set (right after an upload) the review can be limited to what that upload touched.
 * `onChanged` — called after bills are cancelled, so the invoice list behind can reload.
 */
const SalesInvoiceReview = ({ importRunId, onChanged, onClose }) => {
  const [tab, setTab] = useState("credit");
  const [uploadOnly, setUploadOnly] = useState(Boolean(importRunId));
  const [showAllBills, setShowAllBills] = useState(false);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [cancelling, setCancelling] = useState(false);
  const [results, setResults] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get(REVIEW, { importRunId: uploadOnly && importRunId ? importRunId : undefined });
      setReview(data);
      setSelected(new Set());
    } catch (error) {
      toast.error(error.message || "Failed to load the invoice review");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setUploadOnly(Boolean(importRunId));
  }, [importRunId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadOnly, importRunId]);

  // by default only the bills past the limit are listed: those are the ones to cancel
  const creditRows = useMemo(() => (review?.creditLimit || []).filter((row) => showAllBills || row.overLimit), [review, showAllBills]);
  const billRows = useMemo(() => (review?.outstandingBills || []).filter((row) => showAllBills || row.overLimit), [review, showAllBills]);
  const stockRows = review?.stock || [];
  const rowsOfTab = tab === "credit" ? creditRows : tab === "bills" ? billRows : [];
  const selectedHere = rowsOfTab.filter((row) => selected.has(row.invoiceId));

  const customerCount = (rows) => new Set(rows.map((row) => row.customerId)).size;
  const counts = { credit: customerCount(review?.creditLimit || []), bills: customerCount(review?.outstandingBills || []), stock: stockRows.length };

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = (rows, allSelected) =>
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((row) => (allSelected ? next.delete(row.invoiceId) : next.add(row.invoiceId)));
      return next;
    });

  const cancelInvoices = async (rows) => {
    if (rows.length === 0) return;
    const label = rows.length === 1 ? `invoice ${rows[0].invoiceNumber}` : `${rows.length} invoices`;
    const remarks = window.prompt(
      `Cancel ${label}? A credit note is raised for each and its e-way bill / e-invoice is cancelled. This cannot be undone.\n\nReason (optional):`,
      tab === "credit" ? "Customer over credit limit" : "Customer over outstanding bill limit"
    );
    if (remarks === null) return;
    setCancelling(true);
    try {
      const data = await api.post(`${REVIEW}/cancel`, { invoiceIds: rows.map((row) => row.invoiceId), remarks: remarks.trim() || null });
      const list = Array.isArray(data) ? data : [];
      const failed = list.filter((result) => !result.cancelled);
      if (failed.length === 0) toast.success(`${list.length} invoice(s) cancelled`);
      else setResults(list.map((result) => ({ ...result, invoiceNumber: result.invoiceNumber || rows.find((row) => row.invoiceId === result.invoiceId)?.invoiceNumber })));
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error.message || "Failed to cancel the invoices");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="content-card" style={{ marginTop: "12px", marginBottom: "12px", position: "relative" }}>
      <div className="flex items-center justify-between" style={{ marginBottom: "8px" }}>
        <h3 style={{ fontSize: "15px", fontWeight: 700 }}>Invoice Review — credit limits, outstanding bills and stock</h3>
        <button className="close-btn" onClick={onClose}>
          Close
        </button>
      </div>
      {stockRows.some((row) => row.insufficient) ? (
        <div
          role="alert"
          style={{
            background: "var(--color-danger-soft, #fdecea)",
            color: "var(--color-danger, #b42318)",
            border: "1px solid var(--color-danger, #b42318)",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 10,
            fontSize: 13,
          }}
        >
          <strong>Stock alert:</strong> the closing stock of {stockRows.filter((row) => row.insufficient).length} product(s) is less than what the
          {importRunId ? " uploaded" : " open"} invoices need —{" "}
          {stockRows
            .filter((row) => row.insufficient)
            .slice(0, 5)
            .map((row) => `${row.skuCode} ${row.productName} (short by ${qty(row.shortBy)})`)
            .join(", ")}
          {stockRows.filter((row) => row.insufficient).length > 5 ? " and more" : ""}.{" "}
          {importRunId ? "The distributor has been sent an SMS and the alert is listed under Alerts." : ""}
        </div>
      ) : null}

      <div className="filters-bar" style={{ alignItems: "center" }}>
        {TABS.map((t) => (
          <button key={t.key} className={tab === t.key ? "primary" : "secondary"} onClick={() => setTab(t.key)}>
            {t.label} ({counts[t.key]})
          </button>
        ))}
        {importRunId ? (
          <select value={uploadOnly ? "upload" : "all"} onChange={(e) => setUploadOnly(e.target.value === "upload")} aria-label="Scope">
            <option value="upload">Only what the last upload touched</option>
            <option value="all">All customers and products</option>
          </select>
        ) : null}
        {tab !== "stock" ? (
          <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
            <input type="checkbox" checked={showAllBills} onChange={(e) => setShowAllBills(e.target.checked)} />
            Show all outstanding bills of these customers
          </label>
        ) : null}
        <button className="secondary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        {tab !== "stock" ? (
          <button className="primary" onClick={() => cancelInvoices(selectedHere)} disabled={cancelling || selectedHere.length === 0}>
            {cancelling ? "Cancelling..." : `Cancel selected (${selectedHere.length})`}
          </button>
        ) : null}
      </div>

      {tab === "credit" ? (
        <>
          <div className="page-subtitle" style={{ marginTop: "8px" }}>
            Customers whose outstanding bills (including newly uploaded ones) add up to more than their credit limit. Bills are counted oldest
            first; the highlighted ones are those that take the customer past the limit. Customers with no credit limit set are not checked.
          </div>
          <BillGrid kind="credit" rows={creditRows} selected={selected} onToggle={toggle} onToggleAll={toggleAll} onCancelOne={(row) => cancelInvoices([row])} cancelling={cancelling} />
        </>
      ) : null}

      {tab === "bills" ? (
        <>
          <div className="page-subtitle" style={{ marginTop: "8px" }}>
            {review && Number(review.maxOutstandingBills) > 0
              ? `Customers with more than ${review.maxOutstandingBills} outstanding bills (the "Maximum Outstanding Invoices Allowed" setting). The highlighted bills are the newest ones beyond that number.`
              : 'This check is off: set "Maximum Outstanding Invoices Allowed" under Receivables → Settings to turn it on.'}
          </div>
          <BillGrid kind="bills" rows={billRows} selected={selected} onToggle={toggle} onToggleAll={toggleAll} onCancelOne={(row) => cancelInvoices([row])} cancelling={cancelling} />
        </>
      ) : null}

      {tab === "stock" ? (
        <>
          <div className="page-subtitle" style={{ marginTop: "8px" }}>
            Products where the stock on hand, less what open invoices still have to deliver, is short or below the low-stock threshold
            {review ? ` (${qty(review.lowStockThreshold)}; change it under Receivables → Settings)` : ""}. The distributor is sent an SMS when an
            upload causes this.
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th style={{ textAlign: "left" }}>Product</th>
                  <th>UOM</th>
                  <th style={right}>Stock On Hand</th>
                  <th style={right}>To Deliver (open invoices)</th>
                  <th style={right}>Remaining Qty</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {stockRows.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">
                        <span className="empty-state-title">No product is short or below the threshold</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  stockRows.map((row) => (
                    <tr key={row.productId} style={row.insufficient ? { background: "var(--color-danger-soft, #fef3f2)" } : undefined}>
                      <td>{row.skuCode}</td>
                      <td style={{ textAlign: "left" }}>{row.productName}</td>
                      <td>{row.uom || "-"}</td>
                      <td style={right}>{qty(row.onHand)}</td>
                      <td style={right}>{qty(row.toDeliver)}</td>
                      <td style={{ ...right, fontWeight: 600, color: row.insufficient ? "var(--color-danger)" : undefined }}>{qty(row.remaining)}</td>
                      <td>
                        <span className={`badge ${row.insufficient ? "badge-red" : "badge-amber"}`}>
                          {row.insufficient ? `Short by ${qty(row.shortBy)}` : "Low stock"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {results ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setResults(null)}>
          <div className="modal-box" style={{ maxWidth: "760px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Cancellation Results</h3>
              <button className="close-btn" onClick={() => setResults(null)}>
                Close
              </button>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Result</th>
                    <th style={{ textAlign: "left" }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result) => (
                    <tr key={result.invoiceId}>
                      <td>{result.invoiceNumber || `#${result.invoiceId}`}</td>
                      <td>
                        <span className={`badge ${result.cancelled ? "badge-green" : "badge-red"}`}>{result.cancelled ? "Cancelled" : "Not cancelled"}</span>
                      </td>
                      <td style={{ textAlign: "left", fontSize: "12.5px" }}>{result.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button type="button" className="primary" onClick={() => setResults(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SalesInvoiceReview;
